/**
 * Progress Bar Component
 * Reusable progress indicator with smooth animations
 */

import React from 'react';

interface ProgressBarProps {
  progress: number; // 0-100
  label?: string;
  showPercentage?: boolean;
  color?: 'primary' | 'success' | 'warning' | 'error';
  size?: 'small' | 'medium' | 'large';
  striped?: boolean;
  animated?: boolean;
}

export function ProgressBar({
  progress,
  label,
  showPercentage = true,
  color = 'primary',
  size = 'medium',
  striped = false,
  animated = false
}: ProgressBarProps) {
  const clampedProgress = Math.min(100, Math.max(0, progress));

  const colorClasses = {
    primary: 'bg-blue-500',
    success: 'bg-green-500',
    warning: 'bg-yellow-500',
    error: 'bg-red-500'
  };

  const sizeClasses = {
    small: 'h-2',
    medium: 'h-4',
    large: 'h-6'
  };

  return (
    <div className="w-full">
      {label && (
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm text-gray-400">{label}</span>
          {showPercentage && (
            <span className="text-sm font-medium text-white">
              {Math.round(clampedProgress)}%
            </span>
          )}
        </div>
      )}
      
      <div className={`w-full bg-dark-300 rounded-full overflow-hidden ${sizeClasses[size]}`}>
        <div
          className={`
            ${colorClasses[color]} 
            ${sizeClasses[size]} 
            transition-all duration-300 ease-out
            flex items-center justify-end
            ${striped ? 'progress-striped' : ''}
            ${animated && striped ? 'progress-animated' : ''}
          `}
          style={{ width: `${clampedProgress}%` }}
        >
          {size === 'large' && showPercentage && clampedProgress > 10 && (
            <span className="text-xs text-white font-medium mr-2">
              {Math.round(clampedProgress)}%
            </span>
          )}
        </div>
      </div>

      <style>{`
        .progress-striped {
          background-image: linear-gradient(
            45deg,
            rgba(255, 255, 255, 0.15) 25%,
            transparent 25%,
            transparent 50%,
            rgba(255, 255, 255, 0.15) 50%,
            rgba(255, 255, 255, 0.15) 75%,
            transparent 75%,
            transparent
          );
          background-size: 1rem 1rem;
        }

        .progress-animated {
          animation: progress-bar-stripes 1s linear infinite;
        }

        @keyframes progress-bar-stripes {
          0% {
            background-position: 1rem 0;
          }
          100% {
            background-position: 0 0;
          }
        }
      `}</style>
    </div>
  );
}